import React from 'react';
import { Box, Grid, Card, IconButton, Typography, CardContent, CardHeader } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import type { ScenarioContextDto, ScenarioContextFactDto, VariableDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm.ts';
import { createFormField } from '@/app/utils/formUtils.ts';
import VariableSuggestionInput from '../Variables/VariableSuggestionInput.tsx';
import { Add, Delete } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs, I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';

type SceneContextFactsCardProps = {
};

const SceneContextFactsCard: React.FunctionComponent<SceneContextFactsCardProps> = () => {
  const settingsField = createFormField<ScenarioSettingsType>();
  const contextField = createFormField<ScenarioContextDto>();
  const factField = createFormField<ScenarioContextFactDto>();

  const name = `${settingsField.context}.${contextField.facts}` as const;

  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);

  const { control, trigger } = useFormContext<ScenarioSettingsType>();

  const { fields, append, remove } = useFieldArray({
    name,
    control,
    keyName: 'key'
  });

  const defaultFact: ScenarioContextFactDto = {
    text: translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextFactsCard.newFact)),
    id: 0
  };

  return (
    (<Grid item component={Card} xs={12}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Typography variant="h5">
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextFactsCard.facts))}
            </Typography>
            <IconButton onClick={() => append(defaultFact)} size="large">
              <Add />
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        <Grid container>
          {!fields.length && (
            <Grid container justifyContent="center">
              <Typography>
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextFactsCard.noFactsAddedYet))}
              </Typography>
            </Grid>
          )}
          {fields.map((field, index) => {
           const prefix = `${name}.${index}` as const;
           const fullName = `${prefix}.${factField.text}` as const;
           

            return (
              (<Grid container item xs={12} key={field.id} wrap="nowrap">
                <Grid item xs>
                  <Controller
                    control={control}
                    name={fullName}
                    defaultValue={field.text}
                    render={({ field: formField }) => (
                      <VariableSuggestionInput
                        inputProps={{
                          margin: 'normal',
                          InputProps: {
                            inputProps: {
                              maxLength: 100000,
                              maxRows: 30 
                            }
                          }
                        }}
                        value={formField.value}
                        onChange={(value) => formField.onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${prefix}.${factField.id}`}
                    render={() => <input type="hidden" />}
                    defaultValue={field.id}
                  />
                </Grid>
                <Grid item>
                  <IconButton
                    onClick={() => {
                      remove(index);
                      trigger(name);
                    }}
                    size="large"
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>)
            );
          })}
        </Grid>
      </CardContent>
    </Grid>)
  );
};

export default SceneContextFactsCard;
